import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { AuthContext } from './AuthContext';
import operationService from '../services/operationService';

const MenuContext = createContext(null);

const getProfileCode = (user) => {
  if (!user) return null;
  if (user.profileCode) return user.profileCode;
  if (user.vUserTypeCode) return user.vUserTypeCode;
  const role = user.role ?? user.Role;
  if (role === 'Admin' || role === 'ADMIN') return 'ADMIN';
  if (role) return String(role).trim().toUpperCase();
  return 'ADMIN';
};

/**
 * Flatten all menu items (with operationPath) from sections for path matching.
 */
const flattenMenuItems = (menuSections) => {
  if (!Array.isArray(menuSections)) return [];
  const items = [];
  menuSections.forEach((section) => {
    (section.children || []).forEach((item) => {
      if (item.operationPath) {
        items.push({ operationPath: item.operationPath, operationName: item.operationName });
      }
    });
  });
  return items;
};

/**
 * Find the best-matching menu item for pathname (longest matching operationPath).
 * Returns that item's operationName so header title matches sidebar menu name.
 */
const getPageTitleFromMenu = (menuSections, pathname) => {
  const items = flattenMenuItems(menuSections);
  let best = null;
  let bestLength = 0;
  items.forEach((item) => {
    if (pathname === item.operationPath || pathname.startsWith(item.operationPath + '/')) {
      if (item.operationPath.length > bestLength) {
        bestLength = item.operationPath.length;
        best = item;
      }
    }
  });
  return best ? best.operationName : null;
};

export function MenuProvider({ children }) {
  const { user } = useContext(AuthContext);
  const [menuSections, setMenuSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const profileCode = useMemo(() => getProfileCode(user), [user?.profileCode, user?.vUserTypeCode, user?.role, user?.Role]);

  useEffect(() => {
    if (!profileCode) {
      setMenuSections([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    operationService
      .getMenuForProfile(profileCode)
      .then((data) => {
        if (!cancelled) {
          setMenuSections(Array.isArray(data) ? data : []);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || err.message || 'Failed to load menu');
          setMenuSections([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [profileCode]);

  const getPageTitle = (pathname) => {
    const title = getPageTitleFromMenu(menuSections, pathname);
    return title || 'STT';
  };

  const value = useMemo(
    () => ({ menuSections, loading, error, getPageTitle }),
    [menuSections, loading, error]
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
}

export function useMenu() {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('useMenu must be used within MenuProvider');
  return ctx;
}

export default MenuContext;
